'use client';

import { GoogleTagManager } from '@next/third-parties/google';
import { getCookie } from 'cookies-next';
import React, { useMemo } from 'react';

import {
  dataLayerVariables,
  feedbackCookieName,
  gtmID,
} from '@/constants/variables';
import { useUserQuery, useUserRoleQuery } from '@/hooks/queries';
import type { HttpServiceConfig } from '@/services/httpServices/types';
import { WebAppEnvironment } from '@/types/api';

type Props = {
  version: string;
  httpServiceConfig: HttpServiceConfig;
};

const TagManagerProvider: React.FC<Props> = ({
  version = '',
  httpServiceConfig,
}) => {
  const { data: userRoleData } = useUserRoleQuery({
    httpRequestConfig: httpServiceConfig,
  });
  const { data: userObject } = useUserQuery();

  const hasInstructionFeedbackDisabled = useMemo(() => {
    const feedbackCookie = getCookie(feedbackCookieName) as string | undefined;

    if (feedbackCookie) {
      const parsedFeedback = JSON.parse(feedbackCookie) as boolean;
      return !parsedFeedback;
    }

    return true;
  }, []);

  const dataLayer
    = (userRoleData?.role != null
      && userObject?.uid
      && httpServiceConfig.space
      && dataLayerVariables({
        hasInstructionFeedbackDisabled,
        target: httpServiceConfig.target,
        user: {
          roleName: userRoleData.role,
          space: httpServiceConfig.space,
          userId: userObject.uid,
        },
        version,
      }))
      || undefined;

  const isProduction = httpServiceConfig.target === WebAppEnvironment.PRODUCTION;
  const gtmAuth = isProduction === true ? 'U-C_kWvnoXz7hxtGIfo89g' : '_JGM93BQIpKBZKNmE8MTdg';
  const gtmPreview = isProduction === true ? 'env-75' : 'env-74';

  return dataLayer && (
    <GoogleTagManager
      gtmId={gtmID}
      dataLayer={dataLayer}
      auth={gtmAuth}
      preview={gtmPreview}
    />
  );
};

export default TagManagerProvider;
